import React, { useState, useEffect, useCallback } from 'react';
import './Promotions.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TYPE_LABELS = {
  referral: 'Referral Bonus',
  first_deposit: 'First Deposit Bonus',
  seasonal: 'Seasonal Bonus',
};

const Promotions = ({ global = false }) => {
  const [promotions, setPromotions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [settings, setSettings] = useState({ loyalty_rate: 100, bonus_release_rate: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const [type, setType] = useState('first_deposit');
  const [name, setName] = useState('');
  const [bonusRate, setBonusRate] = useState('');
  const [referralPercent, setReferralPercent] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [countryId, setCountryId] = useState('');

  const [loyaltyRate, setLoyaltyRate] = useState('');
  const [bonusReleaseRate, setBonusReleaseRate] = useState('');

  const fetchPromotions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/promotions`, { headers: authHeader() });
      const json = await res.json();
      if (json.success) setPromotions(json.promotions || []);
      else setError(json.message || 'Failed to load promotions');
    } catch (e) {
      setError(e.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/promotions/settings`, { headers: authHeader() });
      const json = await res.json();
      if (json.success && json.settings) {
        setSettings(json.settings);
        setLoyaltyRate(String(json.settings.loyalty_rate));
        setBonusReleaseRate(String(json.settings.bonus_release_rate));
      }
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchSettings();
    if (global) {
      fetch(`${API_BASE}/api/super-admin/countries?getAll=true`, { headers: authHeader() })
        .then(r => r.json())
        .then(j => { if (j.success) setCountries(j.countries || []); })
        .catch(() => {});
    }
  }, [fetchPromotions, fetchSettings, global]);

  const resetForm = () => {
    setType('first_deposit');
    setName('');
    setBonusRate('');
    setReferralPercent('');
    setStartsAt('');
    setEndsAt('');
    setCountryId('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!startsAt || !endsAt) { setError('Start and end dates are required'); return; }
    const payload = {
      type,
      name: name || undefined,
      bonus_rate: type !== 'referral' ? bonusRate : undefined,
      referral_percent: type === 'referral' ? referralPercent : undefined,
      starts_at: startsAt,
      ends_at: endsAt,
      country_id: global && countryId ? countryId : undefined,
    };
    try {
      const res = await fetch(`${API_BASE}/api/promotions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.success) {
        setShowForm(false);
        resetForm();
        fetchPromotions();
      } else setError(json.message || 'Failed to create promotion');
    } catch (e) {
      setError(e.message || 'Failed to create promotion');
    }
  };

  const handleToggle = async (p) => {
    try {
      await fetch(`${API_BASE}/api/promotions/${p.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ is_active: !p.is_active }),
      });
      fetchPromotions();
    } catch (e) { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await fetch(`${API_BASE}/api/promotions/${id}`, { method: 'DELETE', headers: authHeader() });
      fetchPromotions();
    } catch (e) { /* ignore */ }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/promotions/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ loyalty_rate: loyaltyRate, bonus_release_rate: bonusReleaseRate }),
      });
      const json = await res.json();
      if (json.success) {
        setSettings(json.settings);
        alert('Rates updated');
      } else setError(json.message || 'Failed to update rates');
    } catch (e) { setError(e.message); }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <div className="promo-page">
      <div className="promo-head">
        <h2>🏷️ Promotions</h2>
        <button className="promo-create" onClick={() => setShowForm(true)}>+ New Promotion</button>
      </div>

      {error && <div className="promo-error">{error}</div>}

      {global && (
        <div className="promo-settings">
          <h3>🎯 Bonus Criteria (Super Admin only)</h3>
          <label>Loyalty rate — spend $X earns $1 (loyalty wallet)
            <input type="number" value={loyaltyRate} onChange={e => setLoyaltyRate(e.target.value)} />
          </label>
          <label>Bonus release rate — spend $X releases $1 (auto to main wallet)
            <input type="number" value={bonusReleaseRate} onChange={e => setBonusReleaseRate(e.target.value)} />
          </label>
          <button onClick={handleSaveSettings}>Update Criteria</button>
        </div>
      )}

      {showForm && (
        <form className="promo-form" onSubmit={handleCreate}>
          <label>Type
            <select value={type} onChange={e => setType(e.target.value)}>
              <option value="first_deposit">First Deposit Bonus</option>
              <option value="seasonal">Seasonal Bonus</option>
              <option value="referral">Referral Bonus</option>
            </select>
          </label>
          <label>Name (optional)
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Christmas Bonus" />
          </label>
          {type === 'referral' ? (
            <label>Referral % (of first deposit)
              <input type="number" step="0.01" value={referralPercent} onChange={e => setReferralPercent(e.target.value)} placeholder="20" />
            </label>
          ) : (
            <label>Bonus multiplier (× deposit)
              <input type="number" step="0.01" value={bonusRate} onChange={e => setBonusRate(e.target.value)} placeholder="1" />
            </label>
          )}
          <label>Start
            <input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)} />
          </label>
          <label>End
            <input type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)} />
          </label>
          {global && (
            <label>Country (leave blank for Global)
              <select value={countryId} onChange={e => setCountryId(e.target.value)}>
                <option value="">🌍 Global (all countries)</option>
                {countries.map(c => <option key={c.id} value={c.id}>{c.country_name}</option>)}
              </select>
            </label>
          )}
          <button type="submit">Create</button>
          <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
        </form>
      )}

      {loading ? (
        <div className="promo-status">Loading…</div>
      ) : promotions.length === 0 ? (
        <div className="promo-status">No promotions yet.</div>
      ) : (
        <table className="promo-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>Scope</th>
              <th>Rate</th>
              <th>Starts</th>
              <th>Ends</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {promotions.map(p => (
              <tr key={p.id} className={!p.is_active ? 'inactive' : ''}>
                <td>{TYPE_LABELS[p.type] || p.type}</td>
                <td>{p.name || '—'}</td>
                <td>{p.scope === 'global' ? '🌍 Global' : (p.country_name || `#${p.country_id}`)}</td>
                <td>{p.type === 'referral' ? `${p.referral_percent}%` : `×${p.bonus_rate}`}</td>
                <td>{fmtDate(p.starts_at)}</td>
                <td>{fmtDate(p.ends_at)}</td>
                <td>{p.is_active ? '✅ Active' : '⏸ Inactive'}</td>
                <td>
                  <button onClick={() => handleToggle(p)}>{p.is_active ? 'Deactivate' : 'Activate'}</button>
                  <button className="danger" onClick={() => handleDelete(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Promotions;

